import { useCallback, useMemo } from "react"
import { useLanguages } from "lang/languages"
import moment from "moment"

export const dateInBetween = ({ date, startDate, endDate }, offset = 0) => {
  return moment(date).add(offset, "days").isBetween(startDate, endDate, "day", "[]")
}

export const dateToString = (date, format = "YYYY-MM-DD HH:mm:ss") => {
  if (!date || moment(date).format("X") === "0") return ""
  return moment(date).format(format)
}

export const isDateBefore = ({ date, target }, allowSameDay) => {
  const currentDate = date ? new Date(date) : new Date()
  const comparingDate = target ? new Date(target) : new Date()
  const isSameDay = currentDate.toDateString() === comparingDate.toDateString()
  return allowSameDay ? currentDate < comparingDate || isSameDay : currentDate < comparingDate && !isSameDay
}
export const currentIsBefore = (target, allowSameDay) => isDateBefore({ target }, allowSameDay)
export const currentIsAfter = (date, allowSameDay) => isDateBefore({ date }, allowSameDay)

export const calcWeekRange = (date) => [moment(date).startOf("week").toDate(), moment(date).endOf("week").toDate()]

export const daysToTarget = (target, from) => moment(target).add(1, "day").diff(moment(from), "days")

export const calcWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  // Make Sunday's day number 7
  const dayNum = d.getUTCDay() || 7
  // Set to nearest Thursday: current date + 4 - current day number
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  // Calculate full weeks to nearest Thursday
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
}

export const calcYearWeeks = (year) => {
  // December 28 always on the last week
  return calcWeekNumber(new Date(Date.UTC(year, 11, 28)))
}

export const shiftMonths = (date, difference) => {
  const day = new Date(date)
  day.setDate(1) // otherwise, it may be 29, 30, 31, they would leap month
  day.setMonth(date.getMonth() + difference)
  return day
}

export const shiftDays = (date, difference) => {
  const day = new Date(date)
  day.setDate(date.getDate() + difference)
  return day
}

export const formatDate = (date, timezoneAffix = "+08:00") => {
  if (!date) return ""
  // append timezone affix when it is not ISO 8601, it is shame that API does not use UTC
  return new Intl.DateTimeFormat().format(new Date(date + (date.match(/[+-]\d\d:|Z$/) ? "" : timezoneAffix)))
}

export const formatTime = (date, timezoneAffix = "+08:00") => {
  if (!date) return ""
  let time = date
  if (!Number.isInteger(date)) {
    // append timezone affix when it is not ISO 8601, it is shame that API does not use UTC
    time = date + (date.match(/[+-]\d\d:|Z$/) ? "" : timezoneAffix)
  }
  return new Date(time).toLocaleString("default", { hourCycle: "h23", dateStyle: "short", timeStyle: "short" })
}

export const getMonthsForYear = (year) =>
  Array.from({ length: 12 }).map((_, index) => {
    const month = new Date()
    month.setDate(1) // otherwise, it may be 29, 30, 31, they would leap month
    month.setYear(year)
    month.setMonth(index)
    return month
  })

export const getLocalISOString = (date) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000)
  return localDate.toISOString().replace("Z", "")
}

export const useFormat = () => {
  const { language } = useLanguages()

  const formatMonth = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        month: "long",
      }).format,
    [language],
  )

  const formatMonthShort = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        month: "short",
      }).format,
    [language],
  )

  const formatDate = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        month: "short",
        day: "numeric",
      }).format,
    [language],
  )

  //2022/4/6
  const formatFullDate = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }).format,
    [language],
  )

  const formatDateWithWeekDay = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        month: "short",
        day: "numeric",
        weekday: "short",
      }).format,
    [language],
  )

  const formatFullDateWithWeekDay = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        weekday: "short",
      }).format,
    [language],
  )

  const formatSinceDate = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        year: language.startsWith("zh") ? "numeric" : undefined,
        month: "numeric",
        day: "numeric",
      }).format,
    [language],
  )

  const formatUntilDate = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        year: language.startsWith("zh") ? undefined : "numeric",
        month: "numeric",
        day: "numeric",
      }).format,
    [language],
  )

  const formatTime = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        hour: "numeric",
        minute: "numeric",
      }).format,
    [language],
  )

  const formatTimeWith2Digit = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format,
    [language],
  )

  const formatSinceTime = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        hour: "numeric",
        minute: "numeric",
        hour12: false,
      }).format,
    [language],
  )

  const formatUntilTime = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        hour: "numeric",
        minute: "numeric",
        hour12: false,
      }).format,
    [language],
  )

  // 2022/4/6 00:00:00
  const formatDateTime = useCallback((date) => date.toLocaleString(language, { hourCycle: "h23" }), [language])

  const formatDatePeriod = useCallback(
    (since, until) => {
      if (since.getFullYear() === until.getFullYear()) {
        return `${formatSinceDate(since)} ~ ${formatUntilDate(until)}`
      } else {
        return `${formatDate(since)} ~ ${formatDate(until)}`
      }
    },
    [formatSinceDate, formatUntilDate, formatDate],
  )

  const formatTimePeriod = useCallback(
    (since, until) => {
      return `${formatSinceTime(since)} - ${formatUntilTime(until)}`
    },
    [formatSinceTime, formatUntilTime],
  )

  // https://github.com/tc39/proposal-intl-duration-format
  const formatDuration = useCallback(({ days, hours, minutes }) => {
    return `${days ? `${days}D ` : ""}${hours ? `${hours}hr ` : ""}${minutes ? `${minutes}min ` : ""}`
  }, [])

  const getYear = useCallback((date) => date.getFullYear(), [])
  const getMonth = useCallback((date) => date.getMonth() + 1, [])
  const getDate = useCallback((date) => date.getDate(), [])

  return {
    formatMonth,
    formatMonthShort,
    formatDate,
    formatFullDate,
    formatDateWithWeekDay,
    formatFullDateWithWeekDay,
    formatTime,
    formatTimeWith2Digit,
    formatDateTime,
    formatDatePeriod,
    formatTimePeriod,
    formatDuration,
    getYear,
    getMonth,
    getDate,
  }
}

/* below are date-fns functions */

export const isBefore = (date, dateToCompare) => {
  return date.getTime() < dateToCompare.getTime()
}

export const isAfter = (date, dateToCompare) => {
  return date.getTime() > dateToCompare.getTime()
}

export const isSameDay = (leftDate, rightDate) => {
  const leftDay = new Date(leftDate)
  const rightDay = new Date(rightDate)
  leftDay.setHours(0, 0, 0, 0)
  rightDay.setHours(0, 0, 0, 0)
  return leftDay.getTime() === rightDay.getTime()
}

export const startOfWeek = (date) => {
  const startDate = new Date(date)
  const day = date.getDay()
  startDate.setDate(startDate.getDate() - day)
  startDate.setHours(0, 0, 0, 0)
  return startDate
}

export const endOfWeek = (date) => {
  const endDate = new Date(date)
  const day = date.getDay()
  endDate.setDate(endDate.getDate() + (6 - day))
  endDate.setHours(23, 59, 59, 999)
  return endDate
}
